import { useEffect, useRef, useState } from "react";
import { useAlerts, type Alert } from "./useAlerts";

// AlertsBadge sits in the top-right corner of the map view. Shows a small
// pill with the count of currently-active service alerts; click to expand
// a panel listing summary + description for each.
export function AlertsBadge() {
  const { alerts, loading } = useAlerts();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click. Lets the badge feel like a popover instead of
  // a modal — clicking the map dismisses it.
  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);

  const count = alerts.length;
  const hasAny = count > 0;

  return (
    <div className="alerts" ref={containerRef}>
      <button
        type="button"
        className={`alerts-badge ${hasAny ? "has-alerts" : "ok"}`}
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="dialog"
        aria-expanded={open}
        title={
          loading
            ? "Loading service alerts…"
            : hasAny
              ? `${count} active service alert${count === 1 ? "" : "s"}`
              : "No active service alerts"
        }
      >
        <span className="alerts-icon" aria-hidden>
          {hasAny ? "⚠" : "✓"}
        </span>
        <span className="alerts-label">
          {loading
            ? "alerts…"
            : hasAny
              ? `${count} alert${count === 1 ? "" : "s"}`
              : "no alerts"}
        </span>
      </button>
      {open && (
        <div className="alerts-popover" role="dialog" aria-label="Service alerts">
          <div className="alerts-popover-header">
            <span>Service alerts</span>
            <button
              type="button"
              className="alerts-close"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>
          {alerts.length === 0 ? (
            <div className="alerts-empty">
              {loading ? "Loading…" : "No active service alerts."}
            </div>
          ) : (
            <ul className="alerts-list">
              {alerts.map((a) => (
                <AlertItem key={a.id} alert={a} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function AlertItem({ alert }: { alert: Alert }) {
  const [expanded, setExpanded] = useState(false);
  const routes = (alert.affected_routes ?? [])
    .map(routeShortName)
    .filter(Boolean);
  const window = formatActiveWindow(alert.active_from, alert.active_to);
  return (
    <li className={`alerts-item ${severityClass(alert.severity)}`}>
      <button
        type="button"
        className="alerts-item-summary"
        onClick={() => setExpanded((v) => !v)}
      >
        <span className="alerts-item-title">{alert.summary || "Service alert"}</span>
        {routes.length > 0 && (
          <span className="alerts-item-routes">{routes.join(" · ")}</span>
        )}
      </button>
      {expanded && (
        <div className="alerts-item-body">
          {alert.description && (
            <div className="alerts-item-desc">{alert.description}</div>
          )}
          {window && <div className="alerts-item-window">{window}</div>}
          {alert.url && (
            <a
              className="alerts-item-link"
              href={alert.url}
              target="_blank"
              rel="noreferrer noopener"
            >
              Read more →
            </a>
          )}
        </div>
      )}
    </li>
  );
}

// Map agency-prefixed Sound Transit Link route ids back to the
// rider-facing names that appear in the rest of the UI.
export function routeShortName(id: string): string {
  switch (id) {
    case "40_100479":
      return "1 Line";
    case "40_2LINE":
      return "2 Line";
    default:
      return id;
  }
}

export function severityClass(s?: string): string {
  if (!s) return "";
  const v = s.toLowerCase();
  if (v.includes("severe") || v.includes("major")) return "severe";
  if (v.includes("warn") || v.includes("moderate")) return "warn";
  return "info";
}

export function formatActiveWindow(from?: number, to?: number): string {
  if (!from && !to) return "";
  const fmt = (sec: number) =>
    new Date(sec * 1000).toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  if (from && to) return `${fmt(from)} – ${fmt(to)}`;
  if (from) return `From ${fmt(from)}`;
  if (to) return `Until ${fmt(to)}`;
  return "";
}
